import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../services/api";
import errMsg from "../../services/errMsg";

export const fetchCompanyDevices = createAsyncThunk(
  "companyDevices/fetch",
  async ({ companyId, search = "" } = {}, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/companies/${companyId}/devices`, {
        params: { search: search || undefined },
      });
      return { companyId, ...data.data };
    } catch (err) {
      return rejectWithValue(errMsg(err, "Failed to load devices"));
    }
  }
);

export const createCompanyDevice = createAsyncThunk(
  "companyDevices/create",
  async ({ companyId, payload }, { rejectWithValue }) => {
    try {
      const { data } = await api.post(`/companies/${companyId}/devices`, payload);
      return data.data.device;
    } catch (err) {
      return rejectWithValue(errMsg(err, "Failed to create device"));
    }
  }
);

export const updateCompanyDevice = createAsyncThunk(
  "companyDevices/update",
  async ({ id, payload }, { rejectWithValue }) => {
    try {
      const { data } = await api.patch(`/devices/${id}`, payload);
      return data.data.device;
    } catch (err) {
      return rejectWithValue(errMsg(err, "Failed to update device"));
    }
  }
);

export const deleteCompanyDevice = createAsyncThunk(
  "companyDevices/delete",
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/devices/${id}`);
      return id;
    } catch (err) {
      return rejectWithValue(errMsg(err, "Failed to delete device"));
    }
  }
);

export const lookupImei = createAsyncThunk(
  "companyDevices/lookupImei",
  async (imei, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/devices/imei/${imei}`);
      return data.data.device || null;
    } catch (err) {
      return rejectWithValue(errMsg(err, "IMEI lookup failed"));
    }
  }
);

export const reassignImei = createAsyncThunk(
  "companyDevices/reassignImei",
  async ({ imei, confirmIMEI }, { rejectWithValue }) => {
    try {
      await api.delete(`/devices/imei/${imei}`, { data: { confirmIMEI } });
      return imei;
    } catch (err) {
      return rejectWithValue(errMsg(err, "Reassign failed"));
    }
  }
);

// Link an existing device (standalone or from another company) into the
// given company. Backend cascade clears the old company's admins +
// device_order configs and pins the new company / station.
export const assignDeviceToCompany = createAsyncThunk(
  "companyDevices/assignToCompany",
  async ({ id, companyId }, { rejectWithValue }) => {
    try {
      const { data } = await api.patch(`/devices/${id}`, {
        company: companyId,
      });
      return data.data.device;
    } catch (err) {
      return rejectWithValue(errMsg(err, "Assign failed"));
    }
  }
);

// Unlink a device from its company. Keeps the Device row + Powermeter +
// IMEI config; clears company / station / assignedAdmins and sweeps
// device_order rows pointing at this device.
export const unlinkDeviceFromCompany = createAsyncThunk(
  "companyDevices/unlinkFromCompany",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await api.patch(`/devices/${id}`, { company: null });
      return data.data.device;
    } catch (err) {
      return rejectWithValue(errMsg(err, "Unlink failed"));
    }
  }
);

// Swap hardware identities between two devices. Each Device record keeps
// its company/station/admins; the IMEI + name + model + type + serverId
// (etc.) swap. Powermeter + IMEI Configuration are keyed by IMEI and
// follow naturally; logs are not touched.
export const swapDeviceImei = createAsyncThunk(
  "companyDevices/swapImei",
  async ({ sourceImei, targetImei }, { rejectWithValue }) => {
    try {
      const { data } = await api.post(`/devices/swap-imei`, {
        sourceImei,
        targetImei,
      });
      return data.data.devices;
    } catch (err) {
      return rejectWithValue(errMsg(err, "Swap failed"));
    }
  }
);

export const fetchNextServerId = createAsyncThunk(
  "companyDevices/nextServerId",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/devices/next-server-id`);
      return data.data.nextServerId;
    } catch (err) {
      return rejectWithValue(errMsg(err, "Cannot allocate server id"));
    }
  }
);

export const fetchDeviceStatus = createAsyncThunk(
  "companyDevices/status",
  async (imei, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/devices/imei/${imei}/status`);
      return { imei, status: data.data.status };
    } catch (err) {
      return rejectWithValue(errMsg(err, "Status fetch failed"));
    }
  }
);

export const fetchMqttStatus = createAsyncThunk(
  "companyDevices/mqttStatus",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/devices/mqtt-status`);
      return data.data;
    } catch (err) {
      return rejectWithValue(errMsg(err, "MQTT status fetch failed"));
    }
  }
);

export const sendSoftwareUpdate = createAsyncThunk(
  "companyDevices/softwareUpdate",
  async (imei, { rejectWithValue }) => {
    try {
      const { data } = await api.post(
        `/devices/imei/${imei}/software-update`
      );
      return data.data;
    } catch (err) {
      return rejectWithValue(errMsg(err, "Update command failed"));
    }
  }
);

// Trigger a status-SMS request on the device. Backend publishes
// { st: 5, data: "<phone>" } to the IMEI topic via MQTT.
export const sendStatusSms = createAsyncThunk(
  "companyDevices/sendStatusSms",
  async ({ imei, phone }, { rejectWithValue }) => {
    try {
      const { data } = await api.post(
        `/devices/imei/${imei}/send-status`,
        { phone }
      );
      return data.data;
    } catch (err) {
      return rejectWithValue(errMsg(err, "Send status failed"));
    }
  }
);

const initialState = {
  companyId: null,
  items: [],
  loading: false,
  saving: false,
  search: "",
  imeiLookup: { checking: false, conflict: null },
  statusByImei: {},
  mqtt: { configured: false, connected: false, brokerUrl: null, lastError: null, qos: 1 },
  sendingUpdateImei: null,
  error: null,
};

const slice = createSlice({
  name: "companyDevices",
  initialState,
  reducers: {
    resetForCompany(state, { payload }) {
      if (state.companyId !== payload) {
        state.companyId = payload;
        state.items = [];
        state.search = "";
        state.imeiLookup = { checking: false, conflict: null };
        state.statusByImei = {};
        state.error = null;
      }
    },
    setSearch(state, { payload }) {
      state.search = payload;
    },
    clearImeiConflict(state) {
      state.imeiLookup = { checking: false, conflict: null };
    },
    clearError(state) {
      state.error = null;
    },
  },
  extraReducers: (b) => {
    b.addCase(fetchCompanyDevices.pending, (s) => {
      s.loading = true;
      s.error = null;
    })
      .addCase(fetchCompanyDevices.fulfilled, (s, a) => {
        s.loading = false;
        s.companyId = a.payload.companyId;
        s.items = a.payload.items;
      })
      .addCase(fetchCompanyDevices.rejected, (s, a) => {
        s.loading = false;
        s.error = a.payload;
      })
      .addCase(createCompanyDevice.pending, (s) => {
        s.saving = true;
      })
      .addCase(createCompanyDevice.fulfilled, (s, a) => {
        s.saving = false;
        s.items = [a.payload, ...s.items];
      })
      .addCase(createCompanyDevice.rejected, (s, a) => {
        s.saving = false;
        s.error = a.payload;
      })
      .addCase(updateCompanyDevice.pending, (s) => {
        s.saving = true;
      })
      .addCase(updateCompanyDevice.fulfilled, (s, a) => {
        s.saving = false;
        const idx = s.items.findIndex((d) => d._id === a.payload._id);
        if (idx >= 0) s.items[idx] = a.payload;
      })
      .addCase(updateCompanyDevice.rejected, (s, a) => {
        s.saving = false;
        s.error = a.payload;
      })
      .addCase(deleteCompanyDevice.fulfilled, (s, a) => {
        s.items = s.items.filter((d) => d._id !== a.payload);
      })
      .addCase(deleteCompanyDevice.rejected, (s, a) => {
        s.error = a.payload;
      })
      .addCase(lookupImei.pending, (s) => {
        s.imeiLookup.checking = true;
      })
      .addCase(lookupImei.fulfilled, (s, a) => {
        s.imeiLookup.checking = false;
        s.imeiLookup.conflict = a.payload;
      })
      .addCase(lookupImei.rejected, (s) => {
        s.imeiLookup.checking = false;
        s.imeiLookup.conflict = null;
      })
      .addCase(reassignImei.fulfilled, (s) => {
        s.imeiLookup = { checking: false, conflict: null };
      })
      .addCase(assignDeviceToCompany.fulfilled, (s, a) => {
        // If the assigned device is now in the currently-loaded company,
        // surface it in the list. Otherwise let the caller refetch.
        const dev = a.payload;
        const devCompanyId = dev?.company
          ? typeof dev.company === "object"
            ? dev.company._id || dev.company
            : dev.company
          : null;
        if (devCompanyId && devCompanyId.toString() === s.companyId?.toString()) {
          const idx = s.items.findIndex((d) => d._id === dev._id);
          if (idx >= 0) s.items[idx] = dev;
          else s.items = [dev, ...s.items];
        }
        s.imeiLookup = { checking: false, conflict: null };
      })
      .addCase(unlinkDeviceFromCompany.fulfilled, (s, a) => {
        s.items = s.items.filter((d) => d._id !== a.payload._id);
      })
      .addCase(swapDeviceImei.fulfilled, (s, a) => {
        // Both devices may or may not be in the currently-loaded list.
        // Replace any matching rows by _id so the IMEI / name / model
        // changes appear immediately.
        const updated = a.payload || [];
        for (const dev of updated) {
          const idx = s.items.findIndex((d) => d._id === dev._id);
          if (idx >= 0) s.items[idx] = dev;
        }
      })
      .addCase(fetchDeviceStatus.fulfilled, (s, a) => {
        s.statusByImei[a.payload.imei] = a.payload.status;
      })
      .addCase(fetchMqttStatus.fulfilled, (s, a) => {
        s.mqtt = { ...s.mqtt, ...a.payload };
      })
      .addCase(sendSoftwareUpdate.pending, (s, a) => {
        s.sendingUpdateImei = a.meta.arg;
      })
      .addCase(sendSoftwareUpdate.fulfilled, (s) => {
        s.sendingUpdateImei = null;
      })
      .addCase(sendSoftwareUpdate.rejected, (s, a) => {
        s.sendingUpdateImei = null;
        s.error = a.payload;
      });
  },
});

export const {
  resetForCompany,
  setSearch,
  clearImeiConflict,
  clearError,
} = slice.actions;
export default slice.reducer;
